import { api, formDataApi } from "./api";
import type { UploadProgressHandler } from "./postAPI";

/**
 * 게시글 동영상 API (/api/video).
 *
 * ⚠️ 백엔드 API 미구현 상태 — USE_MOCK_VIDEO 가 true 인 동안은 서버를 호출하지 않고
 *    모든 게시글에 public/sample.mp4 한 개만 있는 것처럼 동작한다.
 *    BE 가 준비되면 USE_MOCK_VIDEO 를 false 로 바꾸면 아래 실제 호출 코드가 쓰인다.
 *
 * 요청한 BE 계약 (이미지 /photo API 와 같은 모양)
 * - GET    /api/video/list?postId=           → { data: PostVideo[] }  (listOrder 오름차순)
 * - POST   /api/video?postId=&postCaseId=   multipart file  → { data: PostVideo }
 * - GET    /api/video/file/{pathFileName}    동영상 스트리밍 (Range 요청 지원 필수 — 탐색/이어보기)
 * - GET    /api/video/thumbnail/{thumbnail} 썸네일 이미지 (업로드 시 서버에서 첫 프레임 추출, 선택)
 * - PATCH  /api/video                        { videoId, videoName } 이름 수정
 * - DELETE /api/video/{videoId}
 */
export const USE_MOCK_VIDEO = true;

export interface PostVideo {
  videoId: number;
  postId: number;
  videoName: string;
  /** 서버 저장 파일명 (스트리밍 URL 에 사용) */
  pathFileName: string;
  originalFileName?: string;
  /** 썸네일 파일명 (없으면 목록에서 동영상 첫 프레임을 보여준다) */
  thumbnail?: string | null;
  /** 재생 시간(초) */
  duration?: number | null;
  postCaseId?: number | null;
  mediaGroupId?: number | null;
  listOrder?: number;
}

/** 업로드 허용 확장자 */
export const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov"];

const MOCK_VIDEO_FILE = "sample.mp4";
// public/sample.mp4 를 교체하면 이 값을 올린다. 주소가 그대로면 브라우저가 이전 영상을 캐시에서 계속 재생한다.
const MOCK_VIDEO_VERSION = 2;

const mockVideoList = (postId: number): PostVideo[] => [
  {
    videoId: 1,
    postId,
    videoName: "Video",
    pathFileName: MOCK_VIDEO_FILE,
    originalFileName: MOCK_VIDEO_FILE,
    thumbnail: null,
    duration: null,
    postCaseId: null,
    mediaGroupId: null,
    listOrder: 1,
  },
];

/** 재생용 URL */
export const getVideoFileUrl = (video: Pick<PostVideo, "pathFileName">) =>
  USE_MOCK_VIDEO
    ? `${process.env.PUBLIC_URL}/${video.pathFileName}?v=${MOCK_VIDEO_VERSION}`
    : `${process.env.REACT_APP_BACKEND_URL}/api/video/file/${video.pathFileName}`;

/** 썸네일 URL (없으면 null — 목록에서 동영상 첫 프레임으로 대체) */
export const getVideoThumbnailUrl = (video: Pick<PostVideo, "thumbnail">) =>
  video.thumbnail ? `${process.env.REACT_APP_BACKEND_URL}/api/video/thumbnail/${video.thumbnail}` : null;

export const getVideoList = async (postId: number): Promise<PostVideo[]> => {
  if (USE_MOCK_VIDEO) return mockVideoList(postId);
  try {
    const { data } = await api().get("/video/list", { params: { postId } });
    return data?.data ?? [];
  } catch (e) {
    console.error("getVideoList error:", e);
    return [];
  }
};

export const postVideo = async (
  payload: { postId: number; postCaseId?: number | null; file: File },
  onProgress?: UploadProgressHandler
): Promise<{ success: boolean; data?: PostVideo; message?: string }> => {
  if (USE_MOCK_VIDEO) {
    return { success: false, message: "동영상 업로드 API 가 아직 준비되지 않았습니다." };
  }
  try {
    const { file, ...params } = payload;
    const { data } = await formDataApi().post(
      "/video",
      { file },
      {
        params,
        onUploadProgress: onProgress
          ? (e) => {
              if (e.total) onProgress(e.loaded / e.total);
            }
          : undefined,
      }
    );
    return { success: true, data: data?.data };
  } catch (e: any) {
    console.error("postVideo error:", e);
    return { success: false, message: e.response?.data?.message || "동영상 업로드 중 오류가 발생했습니다." };
  }
};

export const updateVideoName = async (videoId: number, videoName: string) => {
  if (USE_MOCK_VIDEO) return { success: false, message: "동영상 API 가 아직 준비되지 않았습니다." };
  try {
    await api().patch("/video", { videoId, videoName });
    return { success: true };
  } catch (e: any) {
    console.error("updateVideoName error:", e);
    return { success: false, message: e.response?.data?.message || "이름 수정 중 오류가 발생했습니다." };
  }
};

export const deleteVideo = async (videoId: number) => {
  if (USE_MOCK_VIDEO) return { success: false, message: "동영상 API 가 아직 준비되지 않았습니다." };
  try {
    await api().delete(`/video/${videoId}`);
    return { success: true };
  } catch (e: any) {
    console.error("deleteVideo error:", e);
    return { success: false, message: e.response?.data?.message || "삭제 중 오류가 발생했습니다." };
  }
};
